import moment from "moment";
import {difference, uniqBy} from "lodash";
import "./resource";
import { thor } from "../../typings/thor";
import { Enums } from "../../thor.enums";

export class SearchService {
    public static $inject: string[] = [
        "$log",
        "$q",
        "authService",
        "configService",
        "searchResource",
        "sitesService",
        "utilsService"
    ];

    protected apiMaxResult: number;
    protected paginationDefault: number;

    constructor(
        protected $log: ng.ILogService,
        protected $q: ng.IQService,
        protected authService,
        configService,
        protected searchResource,
        protected sitesService,
        protected utilsService
    ) {
        this.apiMaxResult = configService.searchApi.maxLimit;
        this.paginationDefault = configService.pagination.default;
    }

    public getOrganizations(filterParams: thor.SearchOrganizationFilterParams = {}): Promise<thor.Organization[]> {
        const queryParams = this.formatOrganizationQueryParams(
            {
                type: "common_organizations"
            },
            filterParams
        );
        return this.query(queryParams, this.paginationDefault);
    }

    public searchSaleById(saleId, isPast?): Promise<thor.SaleGet> {
        const opts = { id: saleId };
        return (
            isPast
                ? this.searchPastSales(opts)
                : this.searchFutureSales(opts)
        ).then((result: any) => result.items.shift());
    }

    public searchFutureSales(
        filterParams: thor.SearchFilterParams = {},
        _ = {},
        call = null,
        pagination = this.paginationDefault
    ): Promise<thor.SaleGet[]> {
        const queryParams = this.formatSaleQueryParams(
            {
                type: "thor_future_sales",
                "filters[organization]": this.authService.getOrganizationId(),
                [`filters[sites.${this.sitesService.site}.type]`]: "not:online",
                "filters[date]": 1,
                "filters[deleted]": 0,
                sort: "-datetime,address.postcode"
            },
            filterParams
        );

        if (call >= 0) {
            return this.search(queryParams, call, pagination);
        } else {
            return this.query(queryParams, pagination);
        }
    }

    public searchPastSales(
        filterParams: thor.SearchFilterParams = {},
        _ = {},
        call = null,
        pagination = this.paginationDefault
    ): Promise<thor.SaleGet[]> {
        const queryParams = this.formatSaleQueryParams(
            {
                type: "thor_past_sales",
                "filters[organization]": this.authService.getOrganizationId(),
                [`filters[sites.${this.sitesService.site}.type]`]: "not:online",
                "filters[deleted]": 0,
                sort: "-datetime,address.postcode"
            },
            filterParams
        );

        if (typeof call === "number") {
            return this.search(queryParams, call, pagination);
        } else {
            return this.query(queryParams, pagination);
        }
    }

    public searchNoDateSales(
        filterParams: thor.SearchFilterParams = {},
        pagination = this.paginationDefault
    ): Promise<thor.SaleGet[]> {
        const queryParams = this.formatSaleQueryParams(
            {
                type: "thor_future_sales",
                "filters[organization]": this.authService.getOrganizationId(),
                [`filters[sites.${this.sitesService.site}.type]`]: "not:online",
                "filters[date]": 0,
                "filters[deleted]": 0,
                sort: "-datetime,address.postcode"
            },
            filterParams
        );

        return this.query(queryParams, pagination);
    }

    public searchFutureItems(
        filterParams: thor.SearchFilterParams = {},
        sortParams: any = {},
        call = 0,
        pagination = this.paginationDefault
    ): Promise<thor.ItemGet[]> {
        const queryParams = this.formatItemsQueryParams(
            {
                type: "thor_future_items",
                "filters[suppressed]": 0
            },
            filterParams
        );

        if (sortParams.field) {
            let sort = sortParams.field;
            if (sortParams.reverse) {
                sort = "-" + sort;
            }
            queryParams.sort = sort;
        }

        return this.search(queryParams, call, pagination);
    }

    public searchPastItems(
        filterParams: thor.SearchFilterParams = {},
        sortParams: any = {},
        call = 0,
        pagination = this.paginationDefault
    ): Promise<thor.ItemGet[]> {
        const queryParams = this.formatItemsQueryParams(
            {
                type: "thor_past_items",
                "filters[suppressed]": 0,
                "filters[hidden]": 0
            },
            filterParams
        );

        if (sortParams.field) {
            let sort = sortParams.field;
            if (sortParams.reverse) {
                sort = "-" + sort;
            }
            queryParams.sort = sort;
        }

        return this.search(queryParams, call, pagination);
    }

    public searchOnlineContainersFuture(
        filterParams: thor.SearchFilterParams = {},
        call = null,
        pagination = this.paginationDefault
    ): Promise<thor.SaleGet[]> {
        const queryParams = this.formatSaleQueryParams(
            {
                type: "thor_future_sales",
                "filters[organization]": this.authService.getOrganizationId(),
                [`filters[sites.${Enums.Site.Interencheres}.type]`]: "online",
                "filters[deleted]": 0,
                sort: "datetime,address.postcode"
            },
            filterParams
        );

        if (call >= 0) {
            return this.search(queryParams, call, pagination);
        } else {
            return this.query(queryParams, pagination);
        }
    }

    public searchOnlineContainersPast(
        filterParams: thor.SearchFilterParams = {},
        call = null,
        pagination = this.paginationDefault
    ): Promise<thor.SaleGet[]> {
        const queryParams = this.formatSaleQueryParams(
            {
                type: "thor_past_sales",
                "filters[organization]": this.authService.getOrganizationId(),
                [`filters[sites.${Enums.Site.Interencheres}.type]`]: "online",
                "filters[deleted]": 0,
                sort: "-datetime,address.postcode"
            },
            filterParams
        );

        if (typeof call === "number") {
            return this.search(queryParams, call, pagination);
        } else {
            return this.query(queryParams, pagination);
        }
    }

    protected formatOrganizationQueryParams(queryParams: any = {}, filterParams: thor.SearchOrganizationFilterParams) {
        for (const param in filterParams) {
            if (filterParams.hasOwnProperty(param) && filterParams[param]) {
                switch (param) {
                    case "authorized_site":
                        queryParams[`filters[${param}]`] = filterParams[param];
                        break;
                    default:
                        this.$log.error("formatOrganizationQueryParams: unhandled param : " + param);
                }
            }
        }

        return queryParams;
    }

    protected formatSaleQueryParams(queryParams: any = {}, filterParams: thor.SearchFilterParams) {
        for (const param in filterParams) {
            if (filterParams.hasOwnProperty(param) && filterParams[param]) {
                switch (param) {
                    case "query":
                        queryParams[param] = filterParams[param];
                        break;
                    case "from":
                    case "to":
                        queryParams[`filters[${param}]`] = moment(filterParams[param]).format("YYYY-MM-DD");
                        break;
                    case "membership":
                        queryParams[`filters[${param}]`] = `contains:${filterParams[param]}`;
                        break;
                    case "id":
                    case "datetime_range":
                    case "field":
                    case "category":
                    case "hidden":
                    case "canceled":
                    case "site":
                    case `sites.${Enums.Site.Interencheres}.type`:
                    case `sites.${Enums.Site.Auction}.type`:
                    case "is_premium":
                    case "organization":
                    case "premium_count":
                        queryParams[`filters[${param}]`] = filterParams[param];
                        break;
                    default:
                        this.$log.error("formatSaleQueryParams: unhandled param : " + param);
                }
            }
        }

        return queryParams;
    }

    protected formatItemsQueryParams(queryParams: any = {}, filterParams: thor.SearchFilterParams) {
        for (const param in filterParams) {
            if (filterParams.hasOwnProperty(param) && filterParams[param]) {
                switch (param) {
                    case "query":
                        queryParams[param] = filterParams[param];
                        break;
                    case "ids":
                        queryParams[`filters[${param}]`] = filterParams.ids
                            .split(" ")
                            .filter(q => Number.isInteger(Number(q)))
                            .toString();
                        break;
                    case "from":
                    case "to":
                        queryParams[`filters[${param}]`] = moment(filterParams[param]).format("YYYY-MM-DD");
                        break;
                    case "membership":
                        queryParams[`filters[${param}]`] = `contains:${filterParams[param]}`;
                        break;
                    case "mysales":
                        queryParams["filters[organization]"] = this.authService.getOrganizationId();
                        break;
                    case "site":
                    case "field":
                    case "category":
                    case "pricing_lower":
                    case "pricing_upper":
                    case "estimates_lower":
                    case "estimates_upper":
                    case "pricing_is_confirmed":
                    case "highlight":
                    case `sites.${Enums.Site.Interencheres}.type`:
                    case `sites.${Enums.Site.Auction}.type`:
                    case "is_premium":
                    case "organization":
                        queryParams[`filters[${param}]`] = filterParams[param];
                        break;
                    default:
                        this.$log.error("formatItemsQueryParams: unhandled param : " + param);
                }
            }
        }

        return queryParams;
    }

    protected query(params = {}, pagination) {
        return this.searchResource({ "X-Range": this.utilsService.getContentRange(0, pagination) })
            .query(params)
            .$promise
            .then(result => {
                const total = result.metadata.count;
                const apiCalls = Math.ceil(Math.min(total, this.apiMaxResult) / pagination);
                const promises = [];
                promises.push(result);
                for (let i = 1; i < apiCalls; i++) {
                    promises.push(this.queryPart(i, params, pagination));
                }
                return this.$q.all(promises)
                    .then((values: any[]) => {
                        const data = values.reduce(
                                (a: any[], b: any[]) => {
                                    return a.concat(b);
                                },
                                []
                            );

                        const dataNoDuplication = uniqBy(data, "id");
                        const diff = difference(data, dataNoDuplication);

                        if (diff.length > 0) {
                            this.$log.error("Duplication found in list", diff);
                        }

                        return {
                            items: dataNoDuplication,
                            total: total
                        };
                    }).catch(error => {
                        this.$log.error(error);
                        return this.$q.reject(error);
                    });
            }).catch(error => {
                this.$log.error(error);
                return this.$q.reject(error);
            });
    }

    protected search(params = {}, call = 0, pagination) {
        return this.searchResource({ "X-Range": this.utilsService.getContentRange(call, pagination) })
            .query(params)
            .$promise
            .then(result => {
                const total = result.metadata.count;
                const maxApiCalls = Math.ceil(Math.min(total, this.apiMaxResult) / pagination);
                let moreResults = false;

                if (call + 1 < maxApiCalls) {
                    moreResults = true;
                }

                const items = result.reduce(
                    (a: any[], b: any[]) => {
                        return a.concat(b);
                    },
                    []
                );

                const itemsNoDuplication = uniqBy(items, "id");
                const diff = difference(items, itemsNoDuplication);

                if (diff.length > 0) {
                    this.$log.error("Duplication found in list", diff);
                }

                return {
                    items: itemsNoDuplication,
                    total: total,
                    call: call,
                    moreResults: moreResults
                };
            }).catch(error => {
                this.$log.error(error);
            });
    }

    private queryPart(numCall, params = {}, pagination) {
        return this.searchResource({"X-Range": this.utilsService.getContentRange(numCall, pagination)})
            .query(params)
            .$promise;
    }
}

angular
    .module("thor.core.search.service", [
        "thor.core.search.resource"
    ])
    .service("searchService", SearchService);
